import React from "react";
import { Box, Tooltip } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import DarkTypography from "components/common/DarkTypography";
import GradientTooltipCard from "components/common/GradientTooltipCard";
import { joyTooltipCustomContentHostSx } from "styles/globalStyles";

type PlanningTooltipExclusiveContextValue = {
  openTooltipId: string | null;
  toggle: (id: string) => void;
  hoverOpen: (id: string) => void;
  hoverClose: (id: string) => void;
  suppressDismiss: () => void;
};

const PlanningTooltipExclusiveContext =
  React.createContext<PlanningTooltipExclusiveContextValue | null>(null);

const TOOLTIP_HOVER_DELAY = 500;

export function PlanningTooltipExclusiveProvider({ children }: { children: React.ReactNode }) {
  const [openTooltipId, setOpenTooltipId] = React.useState<string | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredIdRef = React.useRef<string | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const toggle = React.useCallback((id: string) => {
    clearTimer();
    setOpenTooltipId((cur) => (cur === id ? null : id));
  }, []);

  const hoverOpen = React.useCallback((id: string) => {
    hoveredIdRef.current = id;
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (hoveredIdRef.current === id) {
        setOpenTooltipId(id);
      }
      timerRef.current = null;
    }, TOOLTIP_HOVER_DELAY);
  }, []);

  const hoverClose = React.useCallback((id: string) => {
    if (hoveredIdRef.current === id) {
      hoveredIdRef.current = null;
    }
    clearTimer();
    setOpenTooltipId((cur) => (cur === id ? null : cur));
  }, []);

  const suppressRef = React.useRef(false);
  const suppressDismiss = React.useCallback(() => {
    suppressRef.current = true;
  }, []);

  React.useEffect(() => () => clearTimer(), []);

  React.useEffect(() => {
    if (!openTooltipId) return;
    const dismiss = () => {
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      setOpenTooltipId(null);
    };
    document.addEventListener("touchstart", dismiss, { passive: true });
    return () => document.removeEventListener("touchstart", dismiss);
  }, [openTooltipId]);

  const value = React.useMemo(
    () => ({ openTooltipId, toggle, hoverOpen, hoverClose, suppressDismiss }),
    [openTooltipId, toggle, hoverOpen, hoverClose, suppressDismiss],
  );
  return (
    <PlanningTooltipExclusiveContext.Provider value={value}>
      {children}
    </PlanningTooltipExclusiveContext.Provider>
  );
}

function usePlanningExclusiveTooltip(tooltipId: string) {
  const ctx = React.useContext(PlanningTooltipExclusiveContext);
  if (!ctx) {
    return {
      controlledOpen: undefined as boolean | undefined,
      onTap: undefined as (() => void) | undefined,
      onHoverOpen: undefined as (() => void) | undefined,
      onHoverClose: undefined as (() => void) | undefined,
    };
  }
  return {
    controlledOpen: ctx.openTooltipId === tooltipId,
    onTap: () => {
      ctx.suppressDismiss();
      ctx.toggle(tooltipId);
    },
    onHoverOpen: () => ctx.hoverOpen(tooltipId),
    onHoverClose: () => ctx.hoverClose(tooltipId),
  };
}

const planningSlotRichTooltipCardSx = {
  border: "none",
  minWidth: { xs: "min(260px, 92vw)", sm: 280 },
  maxWidth: { xs: "min(95vw, 380px)", sm: 380 },
} as const;

const planningSlotTooltipProps = {
  variant: "soft" as const,
  sx: [joyTooltipCustomContentHostSx, { zIndex: 1500 }] as SxProps,
};

export function PlanningSlotTooltipWrap({
  tooltipId,
  tooltipText,
  tooltipHeader,
  tooltipFooter,
  leaveDelay = 0,
  children,
}: {
  tooltipId: string;
  tooltipText: string;
  tooltipHeader: string;
  tooltipFooter?: React.ReactNode;
  leaveDelay?: number;
  children: React.ReactNode;
}) {
  const body = tooltipText.trim();
  const hasHeader = tooltipHeader.trim().length > 0;
  const { controlledOpen, onTap, onHoverOpen, onHoverClose } =
    usePlanningExclusiveTooltip(tooltipId);

  if (!body && !tooltipFooter && !hasHeader) {
    return <>{children}</>;
  }

  const controlled = controlledOpen !== undefined;

  const handleMouseEnter = () => onHoverOpen?.();
  const handleMouseLeave = () => onHoverClose?.();

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onTap?.();
  };

  return (
    <Tooltip
      disableInteractive
      leaveDelay={leaveDelay}
      {...(controlled ? { open: controlledOpen } : {})}
      title={
        <GradientTooltipCard
          header={
            <DarkTypography
              level="body-sm"
              colorVariant="title"
              sx={{ fontWeight: 700, minWidth: 0 }}
            >
              {tooltipHeader}
            </DarkTypography>
          }
          footer={tooltipFooter}
          sx={planningSlotRichTooltipCardSx}
          bodySx={{ minWidth: 0 }}
        >
          {body ? (
            <DarkTypography
              level="body-xs"
              colorVariant="primary"
              sx={{ whiteSpace: "pre-line", textAlign: "left", minWidth: 0 }}
            >
              {body}
            </DarkTypography>
          ) : null}
        </GradientTooltipCard>
      }
      {...planningSlotTooltipProps}
    >
      <Box
        sx={{ minWidth: 0, cursor: "help" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </Box>
    </Tooltip>
  );
}
